import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useCart } from "@/contexts/CartContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ChevronLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

const Checkout = () => {
  const {
    items, subtotal, itemsSubtotal, orderType, postalCode, scheduledTime,
    scheduledDate, isNextDay, clear, promoCode, applyPromoCode, clearPromoCode,
    codePromoData, discountInfo,
  } = useCart();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [codeInput, setCodeInput] = useState(promoCode || "");
  const [codeError, setCodeError] = useState("");
  const [applyingCode, setApplyingCode] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    note: "",
  });

  useEffect(() => {
    if (items.length === 0) {
      navigate("/commander");
      return;
    }
    api.get("/settings").then((r) => setSettings(r.data)).catch(console.error);
    // Prefill from localStorage
    try {
      const raw = localStorage.getItem("quang_customer");
      if (raw) {
        const c = JSON.parse(raw);
        setForm((f) => ({ ...f, ...c }));
      }
    } catch (e) {}
  }, []);

  const postalEntry = useMemo(() => {
    if (!settings || !postalCode) return null;
    const list = Array.isArray(settings.postal_codes) ? settings.postal_codes : [];
    return list.find((p) => String(p.code).trim() === postalCode.trim()) || null;
  }, [settings, postalCode]);

  const deliveryFee = useMemo(() => {
    if (orderType !== "delivery") return 0;
    return postalEntry ? Number(postalEntry.delivery_fee) : 0;
  }, [postalEntry, orderType]);

  const minOrder = useMemo(() => {
    return postalEntry ? Number(postalEntry.min_order) : 20;
  }, [postalEntry]);

  const postalInvalid = orderType === "delivery" && !!postalCode && !postalEntry && settings != null;

  const total = subtotal + deliveryFee;

  const handleChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error("Merci de remplir tous les champs requis");
      return;
    }
    if (orderType === "delivery" && (!form.address || !postalCode)) {
      toast.error("Adresse et code postal requis pour la livraison");
      return;
    }
    if (postalInvalid) {
      toast.error("Le code postal n'est pas livrable. En cas de doute, merci de nous appeler directement au 078 616 29 29.");
      return;
    }
    if (orderType === "pickup" && !form.phone) {
      toast.error("Téléphone requis pour le retrait");
      return;
    }
    if (orderType === "delivery" && itemsSubtotal < minOrder) {
      toast.error(`Minimum ${minOrder} CHF pour livraison`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        order_type: orderType,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        address: orderType === "delivery" ? form.address.trim() : "",
        postal_code: orderType === "delivery" ? postalCode : "",
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          selected_addons: i.selected_addons || [],
          comment: i.comment || "",
        })),
        note: form.note.trim(),
        scheduled_time: scheduledTime || "asap",
        scheduled_date: scheduledDate || "",
        is_next_day: !!isNextDay,
        promo_code: promoCode || "",
        marketing_opt_in: marketingOptIn,
      };
      const { data } = await api.post("/orders", payload);
      // Save customer "account"
      localStorage.setItem(
        "quang_customer",
        JSON.stringify({
          email: payload.email,
          first_name: payload.first_name,
          last_name: payload.last_name,
          phone: payload.phone,
        })
      );
      localStorage.setItem("quang_last_order_id", data.id);
      clear();
      toast.success("Commande passée avec succès !");
      navigate(`/mes-commandes?email=${encodeURIComponent(payload.email)}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors de la commande";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Header />

      <div className="max-w-5xl mx-auto px-5 md:px-8 lg:px-12 py-8 md:py-12">
        <Link to="/commander" className="inline-flex items-center text-sm text-neutral-600 hover:text-black mb-6" data-testid="back-to-menu">
          <ChevronLeft className="w-4 h-4 mr-1" /> Retour au menu
        </Link>

        <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Finaliser la commande
        </h1>
        <p className="text-sm text-neutral-500 mb-4">
          {orderType === "delivery" ? "Livraison à votre adresse" : "À emporter sur place"}
        </p>

        {orderType === "pickup" && (
          <div
            data-testid="checkout-pickup-address"
            className="mb-6 flex items-start gap-3 bg-[#E9C46A]/20 border-2 border-[#E9C46A] rounded-xl px-4 py-3"
          >
            <span className="text-2xl">⚠️</span>
            <div className="text-sm leading-snug">
              <p className="font-black uppercase tracking-wider text-xs text-[#9b6f10] mb-0.5">
                Attention · Nous avons changé d&apos;adresse !
              </p>
              <p className="text-neutral-900 font-bold">
                Rue Pré-du-Marché 15, 1004 Lausanne
              </p>
              <p className="text-xs text-neutral-700 mt-0.5">
                Merci de bien noter notre nouvelle adresse avant de venir récupérer votre commande.
              </p>
            </div>
          </div>
        )}

        {isNextDay && scheduledDate && (
          <div
            data-testid="checkout-next-day-banner"
            className="mb-6 flex items-start gap-3 bg-[#E9C46A]/25 border-2 border-[#E9C46A] rounded-xl px-4 py-3"
          >
            <span className="text-2xl">📅</span>
            <div>
              <p className="text-sm font-black uppercase tracking-wider">Commande pour demain</p>
              <p className="text-xs text-neutral-800 mt-0.5">
                Votre commande sera préparée le <strong>{scheduledDate}</strong>{scheduledTime && scheduledTime !== "asap" ? <> à <strong>{scheduledTime}</strong></> : null}.
              </p>
            </div>
          </div>
        )}

        {postalInvalid && (
          <div
            data-testid="checkout-postal-invalid"
            className="mb-6 bg-red-50 border-2 border-red-300 text-[#BF0424] rounded-xl px-4 py-3 text-sm"
          >
            Le code postal <strong>{postalCode}</strong> n&apos;est pas livrable. En cas de doute, merci de nous appeler directement au <strong>078&nbsp;616&nbsp;29&nbsp;29</strong>.
          </div>
        )}

        <div className="mb-8" />

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-5">
            <div className="bg-white rounded-xl border border-neutral-200 p-6">
              <h3 className="font-bold text-base mb-4">Vos informations</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name" className="text-xs uppercase tracking-wider font-bold">Prénom *</Label>
                  <Input id="first_name" data-testid="input-first-name" value={form.first_name} onChange={handleChange("first_name")} required className="mt-1.5 border-neutral-300 focus-visible:ring-[#D90429]" />
                </div>
                <div>
                  <Label htmlFor="last_name" className="text-xs uppercase tracking-wider font-bold">Nom *</Label>
                  <Input id="last_name" data-testid="input-last-name" value={form.last_name} onChange={handleChange("last_name")} required className="mt-1.5 border-neutral-300 focus-visible:ring-[#D90429]" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider font-bold">E-mail *</Label>
                  <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={handleChange("email")} required className="mt-1.5 border-neutral-300 focus-visible:ring-[#D90429]" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-xs uppercase tracking-wider font-bold">
                    Téléphone {orderType === "pickup" ? "*" : ""}
                  </Label>
                  <Input id="phone" data-testid="input-phone" value={form.phone} onChange={handleChange("phone")} required={orderType === "pickup"} className="mt-1.5 border-neutral-300 focus-visible:ring-[#D90429]" />
                </div>
              </div>
            </div>

            {orderType === "delivery" && (
              <div className="bg-white rounded-xl border border-neutral-200 p-6">
                <h3 className="font-bold text-base mb-4">Adresse de livraison</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-2">
                    <Label htmlFor="address" className="text-xs uppercase tracking-wider font-bold">Adresse *</Label>
                    <Input id="address" data-testid="input-address" value={form.address} onChange={handleChange("address")} required placeholder="Rue, numéro" className="mt-1.5 border-neutral-300 focus-visible:ring-[#D90429]" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold">Code postal</Label>
                    <Input value={postalCode} disabled data-testid="display-postal-code" className="mt-1.5 bg-neutral-100" />
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl border border-neutral-200 p-6">
              <h3 className="font-bold text-base mb-4">Note de commande <span className="text-xs font-normal text-neutral-500">(optionnel)</span></h3>
              <Textarea
                data-testid="input-note"
                value={form.note}
                onChange={handleChange("note")}
                placeholder="Pas de coriandre, sauce à part…"
                className="border-neutral-300 focus-visible:ring-[#D90429] min-h-[80px]"
              />
            </div>

            {/* Marketing opt-in */}
            <label
              htmlFor="marketing-opt-in"
              data-testid="marketing-optin-row"
              className="flex items-start gap-3 bg-white rounded-xl border border-neutral-200 p-5 cursor-pointer hover:border-[#D90429] transition-colors"
            >
              <input
                id="marketing-opt-in"
                data-testid="marketing-optin-checkbox"
                type="checkbox"
                checked={marketingOptIn}
                onChange={(e) => setMarketingOptIn(e.target.checked)}
                className="mt-1 w-5 h-5 accent-[#D90429] flex-shrink-0 cursor-pointer"
              />
              <div className="text-sm leading-snug">
                <p className="font-bold text-neutral-900">
                  Recevoir nos offres et réductions par e-mail
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  J'accepte que Quang utilise mon e-mail pour m'envoyer des promotions occasionnelles. Désinscription possible à tout moment.
                </p>
              </div>
            </label>
          </div>

          {/* Order summary */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-xl border border-neutral-200 p-6 sticky top-24">
              <h3 className="font-bold text-base mb-4">Résumé</h3>
              <div className="space-y-2 max-h-60 overflow-y-auto mb-4 pr-2">
                {items.map((it) => (
                  <div key={it.product_id} className="flex justify-between text-sm">
                    <span className="text-neutral-700">{it.quantity} × {it.name}</span>
                    <span className="font-semibold whitespace-nowrap">{(it.price * it.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* Promo code input */}
              <div className="border-t border-neutral-200 pt-4 mb-4" data-testid="promo-code-section">
                <Label className="text-xs uppercase tracking-wider font-bold">Code promo</Label>
                {codePromoData ? (
                  <div
                    data-testid="promo-applied-badge"
                    className="mt-2 flex items-center justify-between gap-2 bg-[#2A9D8F]/15 border border-[#2A9D8F]/40 rounded-md px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-xs font-black text-[#0F766E] truncate">
                        Code « {promoCode} » appliqué
                      </p>
                      <p className="text-[11px] text-neutral-700 truncate">{codePromoData.name}</p>
                    </div>
                    <button
                      type="button"
                      data-testid="remove-promo-code-btn"
                      onClick={() => { clearPromoCode(); setCodeInput(""); setCodeError(""); }}
                      className="text-xs font-bold text-[#D90429] hover:underline"
                    >
                      Retirer
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="mt-2 flex gap-2">
                      <Input
                        data-testid="promo-code-checkout-input"
                        value={codeInput}
                        onChange={(e) => { setCodeInput(e.target.value.toUpperCase().replace(/\s+/g, "")); setCodeError(""); }}
                        placeholder="Ex : BIENVENUE10"
                        className="flex-1 border-neutral-300 focus-visible:ring-[#D90429] uppercase"
                      />
                      <Button
                        type="button"
                        data-testid="apply-promo-code-btn"
                        disabled={!codeInput.trim() || applyingCode}
                        onClick={async () => {
                          setApplyingCode(true);
                          setCodeError("");
                          const res = await applyPromoCode(codeInput);
                          if (!res.ok) {
                            setCodeError(res.message);
                            toast.error(res.message);
                          } else {
                            toast.success("Code promo appliqué !");
                          }
                          setApplyingCode(false);
                        }}
                        className="bg-[#0A0A0A] hover:bg-neutral-800 text-white px-4 font-bold"
                      >
                        {applyingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : "Appliquer"}
                      </Button>
                    </div>
                    {codeError && (
                      <p data-testid="promo-code-error" className="mt-2 text-[11px] text-[#BF0424]">{codeError}</p>
                    )}
                  </>
                )}
              </div>

              <div className="border-t border-neutral-200 pt-3 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral-600">Sous-total</span>
                  <span data-testid="checkout-items-subtotal">{itemsSubtotal.toFixed(2)} CHF</span>
                </div>
                {discountInfo?.totalDiscount > 0 && (
                  <div className="flex justify-between text-[#2A9D8F]" data-testid="checkout-discount-row">
                    <span className="font-bold">
                      Remise{discountInfo.applied?.length > 0 ? ` · ${discountInfo.applied.join(" · ")}` : ""}
                    </span>
                    <span className="font-bold" data-testid="checkout-discount-total">-{discountInfo.totalDiscount.toFixed(2)} CHF</span>
                  </div>
                )}
                {orderType === "delivery" && (
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Livraison</span>
                    <span data-testid="checkout-delivery-fee">{deliveryFee.toFixed(2)} CHF</span>
                  </div>
                )}
                <div className="flex justify-between font-black text-lg pt-2 border-t border-neutral-100">
                  <span>Total</span>
                  <span data-testid="checkout-total">{total.toFixed(2)} CHF</span>
                </div>
              </div>
              <p className="text-xs text-neutral-500 mt-4">Paiement sur place (espèces ou carte selon disponibilité).</p>
              <div
                data-testid="checkout-callback-warning"
                className="mt-4 flex items-start gap-2 bg-[#E9C46A]/20 border border-[#E9C46A] rounded-md px-3 py-2.5 text-[11px] leading-snug text-neutral-800"
              >
                <span className="text-base leading-none">⏱️</span>
                <div>
                  <p className="font-black uppercase tracking-wider text-[10px] text-[#9b6f10]">
                    Confirmation sous 2 min
                  </p>
                  <p className="mt-0.5">
                    Si vous n&apos;avez pas de confirmation de votre commande dans les <strong>2 minutes</strong>,
                    merci de nous appeler directement au{" "}
                    <a href="tel:+41786162929" className="font-black underline whitespace-nowrap">
                      078&nbsp;616&nbsp;29&nbsp;29
                    </a>.
                  </p>
                </div>
              </div>
              <Button
                type="submit"
                data-testid="submit-order-btn"
                disabled={submitting}
                className="w-full mt-5 bg-[#D90429] hover:bg-[#BF0424] text-white rounded-md h-12 font-bold tracking-wide"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmer la commande"}
              </Button>
            </div>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default Checkout;
